import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import { useNavigate } from "react-router";
import MainBox from "../../../container/MainBox";
import DataTable from "../../../../../component/DataTable";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip, PersonCell } from "../../../../../component/listing";
import { ensureOrgEmployees, ensureLegalUnits } from "../../../../../services/useOrgData";
import { getPayrollRuns, getAllSalaryStructures } from "../../../../../services/payroll";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont, motion } from "../../../../../theme";
import { pressable } from "../../../../../component/pressableProps";
import { counterRowSx } from "../../../container/frameLayout";
import {
  money,
  moneyExact,
  periodLabel,
  dayMonth,
  RUN_STATUS_LABEL,
  RUN_STATUS_TONE,
  dashboardStats,
  netTrend,
  trendChange,
  needsAttention,
  byPeriodDesc,
} from "./payrollFacts";

/**
 * The payroll dashboard.
 *
 * The book is explicit that this screen has no stage timeline: "Stages describe
 * the progress of one run, and this screen spans every run, so the only honest
 * summary of where a run has got to is its status badge." It answers three
 * things — who cannot be paid, what the open run comes to, and what the last few
 * came to — and hands off to the run for everything else.
 */

const TONE_INK = {
  danger: tokens.danger,
  warning: tokens.warningText,
  success: tokens.successText,
};

const Counter = ({ label, value, sub, tone, onClick }) => (
  <Box
    {...(onClick ? pressable(onClick) : {})}
    sx={{
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "1rem 1.125rem",
      cursor: onClick ? "pointer" : "default",
      transition: `border-color ${motion.duration[1]}ms ${motion.easing.standard}`,
      "&:hover": onClick ? { borderColor: tokens.primary } : {},
    }}
  >
    <Box
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.textMuted,
        mb: "0.625rem",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: displayFont,
        fontSize: "1.375rem",
        fontWeight: 700,
        lineHeight: 1.15,
        fontVariantNumeric: "tabular-nums",
        color: TONE_INK[tone] || tokens.text,
      }}
    >
      {value}
    </Box>
    {sub != null && (
      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.25rem" }}>{sub}</Box>
    )}
  </Box>
);

const Panel = ({ title, caption, right, children }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: "20rem",
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "1.375rem",
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: "1rem" }}>
      <Box>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
          {title}
        </Box>
        {caption && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.1875rem" }}>{caption}</Box>
        )}
      </Box>
      {right}
    </Stack>
    {children}
  </Box>
);

/** A labelled figure from the open run, read as a row of facts. */
const RunFact = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: "0.5rem", borderBottom: `1px solid ${tokens.border}` }}>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{label}</Box>
    <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Box>
  </Stack>
);

const TrendChart = ({ points }) => {
  const peak = Math.max(1, ...points.map((p) => p.amount));
  const magnitude = Math.pow(10, Math.floor(Math.log10(peak)));
  const top = Math.ceil(peak / (magnitude / 2)) * (magnitude / 2) || 1;
  const ticks = Array.from({ length: 5 }, (_, i) => (top / 4) * (4 - i));

  return (
    <Stack direction="row" spacing={1.5}>
      <Stack
        sx={{
          width: "3.25rem",
          flex: "none",
          height: "9rem",
          justifyContent: "space-between",
          alignItems: "flex-end",
          fontSize: "0.6875rem",
          color: tokens.textMuted,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {ticks.map((t) => (
          <Box key={t}>{t === 0 ? "0" : money(t)}</Box>
        ))}
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1.75} sx={{ height: "9rem", borderBottom: `1px solid ${tokens.borderStrong}` }}>
          {points.map((p, i) => {
            const last = i === points.length - 1;
            return (
              <Tooltip key={p.key} title={`${p.label} · ${moneyExact(p.amount)}`}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", gap: "0.4375rem" }}>
                  {last && (
                    <Box sx={{ fontSize: "0.6875rem", color: tokens.textMuted, fontVariantNumeric: "tabular-nums" }}>
                      {money(p.amount)}
                    </Box>
                  )}
                  <Box
                    sx={{
                      width: "100%",
                      height: `${Math.max(1, (p.amount / top) * 100)}%`,
                      background: last ? tokens.primary : tokens.mutedDark,
                      borderRadius: "4px 4px 0 0",
                      transition: `height ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1.75} sx={{ mt: "0.5rem" }}>
          {points.map((p, i) => (
            <Box
              key={p.key}
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: "0.6875rem",
                color: tokens.textMuted,
                fontWeight: i === points.length - 1 ? 600 : 400,
              }}
            >
              {p.label.split(" ")[0]}
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [runs, setRuns] = useState([]);
  const [structures, setStructures] = useState([]);
  const [roster, setRoster] = useState([]);
  const [legalUnits, setLegalUnits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [r, s, e, lu] = await Promise.all([
        getPayrollRuns(),
        getAllSalaryStructures(),
        ensureOrgEmployees(),
        // The entity count is a detail of one counter's sub-line; losing it
        // must not fail the screen.
        ensureLegalUnits().catch(() => null),
      ]);
      if (r?.success === false) throw new Error(r?.message || "Failed to load");
      setRuns(Array.isArray(r?.data) ? r.data : []);
      setStructures(Array.isArray(s?.data) ? s.data : []);
      setRoster(Array.isArray(e) ? e : []);
      setLegalUnits(Array.isArray(lu) ? lu.length : 0);
    } catch (err) {
      setFailed(true);
      notify.error(err?.message || "Failed to load payroll dashboard");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const structuredIds = useMemo(
    () => structures.map((s) => String(s.employee?._id || s.employee || "")),
    [structures]
  );
  const stats = useMemo(
    () => dashboardStats(runs, { roster, structuredIds, legalUnits }),
    [runs, roster, structuredIds, legalUnits]
  );
  const trend = useMemo(() => netTrend(runs), [runs]);
  const change = useMemo(() => trendChange(trend), [trend]);
  const attention = useMemo(
    () => needsAttention(stats.open, stats.noStructure.people),
    [stats]
  );
  const recent = useMemo(() => [...runs].sort(byPeriodDesc).slice(0, 6), [runs]);

  const open = stats.open;

  return (
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "flex-start" }}
        spacing={1.5}
        sx={{ mb: "1.25rem" }}
      >
        <Box>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text }}>
            Payroll
          </Box>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mt: "0.25rem" }}>
            {loading
              ? " "
              : [
                  `${runs.length} ${runs.length === 1 ? "run" : "runs"}`,
                  open?.scheduledPayDate ? `next pay date ${dayMonth(open.scheduledPayDate)}` : null,
                ].filter(Boolean).join(" · ")}
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <BtnSecondary to="/app/settings/payroll/run/off-cycle/new" sx={{ borderRadius: "8px" }}>
            Off-cycle run
          </BtnSecondary>
          {/* Exactly one primary action, and it is not marigold: opening a run
              is entirely reversible. The irreversible button lives inside. */}
          {open && (
            <BtnPrimary
              onClick={() => navigate(`/app/settings/payroll/run/${open._id}`)}
              sx={{ borderRadius: "8px" }}
            >
              Go to the {periodLabel(open.month, open.year).split(" ")[0]} run
            </BtnPrimary>
          )}
        </Stack>
      </Stack>

      {/* One row of five. The hand-rolled ladder this replaced used MUI's `lg`,
          which is 1200 — so between 1024 and 1200 it silently became two rows,
          which the book forbids. */}
      <Box sx={{ ...counterRowSx(5), mb: "1.25rem" }}>
        <Counter
          label="On payroll"
          value={stats.onPayroll.count}
          sub={stats.onPayroll.legalUnits ? `across ${stats.onPayroll.legalUnits} legal ${stats.onPayroll.legalUnits === 1 ? "entity" : "entities"}` : "active employees"}
        />
        <Counter
          label="No salary structure"
          value={stats.noStructure.count}
          sub={stats.noStructure.count ? "cannot be paid" : "everyone is set up"}
          tone={stats.noStructure.count > 0 ? "danger" : undefined}
          onClick={stats.noStructure.count ? () => navigate("/app/settings/payroll/structures") : undefined}
        />
        <Counter
          label="Pending approval"
          value={stats.pendingApproval.count}
          sub={stats.pendingApproval.isPending ? `in the ${stats.pendingApproval.period} run` : "nothing awaiting approval"}
          tone={stats.pendingApproval.count > 0 ? "warning" : undefined}
        />
        <Counter
          label="Last net payout"
          value={stats.lastPayout ? money(stats.lastPayout.amount) : "—"}
          sub={stats.lastPayout ? `${stats.lastPayout.period}, ${stats.lastPayout.people} people` : "no run has been paid yet"}
        />
        <Counter
          label="Statutory liability"
          value={stats.statutory ? money(stats.statutory.amount) : "—"}
          // The book prints a filing deadline here. Nothing in the data carries
          // one, so the counter names the period it covers instead.
          sub={stats.statutory ? `employer cost, ${stats.statutory.period}` : "no completed run"}
        />
      </Box>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
        <Panel
          title="The open run"
          right={open ? <StatusChip label={RUN_STATUS_LABEL[open.status]} tone={RUN_STATUS_TONE[open.status]} /> : null}
        >
          {open ? (
            <>
              <RunFact label="Period" value={periodLabel(open.month, open.year)} />
              <RunFact label="Pay date" value={dayMonth(open.scheduledPayDate) || "not set"} />
              <RunFact label="People" value={open.totals?.employeeCount || 0} />
              <RunFact label="Gross" value={moneyExact(open.totals?.totalGross)} />
              <RunFact label="Net" value={moneyExact(open.totals?.totalNet)} />
              <Box sx={{ mt: "1rem" }}>
                <BtnSecondary
                  onClick={() => navigate(`/app/settings/payroll/run/${open._id}`)}
                  sx={{ borderRadius: "8px" }}
                >
                  Open the run
                </BtnSecondary>
              </Box>
            </>
          ) : (
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              No run is open. Every run has been paid or rejected.
            </Box>
          )}
        </Panel>

        <Panel
          title="Net payout trend"
          caption={`${trend.length} completed ${trend.length === 1 ? "run" : "runs"}, excluding the open one`}
          right={
            change != null ? (
              <Box sx={{ textAlign: "right" }}>
                <Box
                  sx={{
                    fontFamily: displayFont,
                    fontSize: "1.375rem",
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                    color: tokens.text,
                  }}
                >
                  {change.change >= 0 ? "+" : ""}{(change.change * 100).toFixed(1)}%
                </Box>
                <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>since {change.from}</Box>
              </Box>
            ) : null
          }
        >
          {trend.length ? (
            <TrendChart points={trend} />
          ) : (
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
              No run has been paid yet, so there is no trend to show.
            </Box>
          )}
        </Panel>
      </Stack>

      {attention.length > 0 && (
        <Box sx={{ mb: "1.25rem" }}>
          <Panel
            title="Needs attention"
            caption={
              open?.scheduledPayDate
                ? `before ${dayMonth(open.scheduledPayDate)}`
                : "before the next pay date"
            }
          >
            {attention.map((row, i) => (
              <Stack
                key={String(row.employee?._id || i)}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  py: "0.75rem",
                  borderTop: i === 0 ? "none" : `1px solid ${tokens.border}`,
                }}
              >
                <PersonCell
                  name={`${row.employee?.fname || ""} ${row.employee?.lname || ""}`.trim() || "Unknown"}
                  secondary={row.employee?.designation || ""}
                  src={row.employee?.profileImage}
                />
                <Box sx={{ flex: 1 }} />
                <StatusChip label={row.reason} tone={row.tone} />
                <BtnSecondary
                  onClick={() => navigate(`/app/employee-details/${row.employee?._id}/compensation`)}
                  sx={{ borderRadius: "6px", px: "0.75rem", py: "0.375rem", fontSize: "0.8125rem", minWidth: 0 }}
                >
                  Fix now
                </BtnSecondary>
              </Stack>
            ))}
          </Panel>
        </Box>
      )}

      <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text, mb: "1rem" }}>
        Recent runs
      </Box>

      <DataTable
        rows={recent}
        loading={loading}
        error={failed}
        onRetry={fetchAll}
        errorTitle="Couldn't load payroll runs"
        empty={{ title: "No payroll runs yet", description: "Create a run to get started." }}
        onRowClick={(r) => navigate(`/app/settings/payroll/run/${r._id}`)}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          {
            key: "period",
            label: "Period",
            width: "22%",
            cellSx: { fontWeight: 600 },
            render: (r) =>
              r.runType === "off_cycle"
                ? `${periodLabel(r.month, r.year)} · off-cycle`
                : periodLabel(r.month, r.year),
          },
          {
            key: "people",
            label: "People",
            align: "right",
            width: "6rem",
            cellSx: { fontVariantNumeric: "tabular-nums" },
            render: (r) => r.totals?.employeeCount || 0,
          },
          {
            key: "gross",
            label: "Gross",
            align: "right",
            cellSx: { fontVariantNumeric: "tabular-nums" },
            render: (r) => moneyExact(r.totals?.totalGross),
          },
          {
            key: "net",
            label: "Net",
            align: "right",
            cellSx: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
            render: (r) => moneyExact(r.totals?.totalNet),
          },
          {
            key: "status",
            label: "Status",
            width: "10rem",
            // The only honest summary of where a run has got to, on a screen
            // that spans every run. No stage timeline.
            render: (r) => <StatusChip label={RUN_STATUS_LABEL[r.status] || r.status} tone={RUN_STATUS_TONE[r.status]} />,
          },
        ]}
      />
    </MainBox>
  );
};

export default PayrollDashboard;
